import { createRoot } from "react-dom/client";
import App from "./App.tsx";
// Self-hosted fonts — eliminates the Google Fonts DNS + roundtrip on first paint.
// font-display: swap is the default in @fontsource so text renders immediately.
import "@fontsource/sora/400.css";
import "@fontsource/sora/500.css";
import "@fontsource/sora/600.css";
import "@fontsource/sora/700.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/dm-serif-display/400.css";
import "./index.css";
import "./i18n/config";
import { initSentry } from "./lib/sentry";
import {
  captureAiReferrer,
  hasFiredAiVisitEvent,
  markAiVisitEventFired,
} from "./lib/ai-referrer";
import { trackEvent } from "./lib/analytics";

// Initialize error tracking AFTER first paint to avoid blocking LCP.
// Sentry + Replay integration is ~38 KB; deferring it keeps the critical path lean.
const scheduleIdle: (cb: () => void) => void =
  typeof window !== "undefined" && "requestIdleCallback" in window
    ? (cb) => (window as unknown as { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(cb)
    : (cb) => setTimeout(cb, 1500);
scheduleIdle(() => {
  initSentry();
});

// Capture referral code before React renders (avoids race with Navigate)
const urlParams = new URLSearchParams(window.location.search);
const ref = urlParams.get("ref");
if (ref) {
  localStorage.setItem("td_ref", ref);
}

// AEO: detect AI-assistant referrers (ChatGPT, Perplexity, Claude, Gemini)
// and fire a one-shot ai_citation_visit event so we can measure attribution.
const aiRef = captureAiReferrer();
if (aiRef && !hasFiredAiVisitEvent()) {
  markAiVisitEventFired();
  // Fire-and-forget; analytics handles consent + errors internally.
  trackEvent("ai_citation_visit", {
    source: aiRef.source,
    via: aiRef.via,
    landing_page: aiRef.landing_page,
  });
}

createRoot(document.getElementById("root")!).render(<App />);
